import { useState, useEffect, useRef, ChangeEvent, FocusEvent } from 'react';

type UseEditableTextareaOptions = {
  initialValue?: string;
  onSubmit?: (value: string) => Promise<void> | void;
};

export const useEditableTextarea = ({
  initialValue = '',
  onSubmit,
}: UseEditableTextareaOptions) => {
  const [value, setValue] = useState(initialValue);
  const [tempValue, setTempValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempValue(e.target.value);
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const submitEdit = async (
    e?:
      | React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
      | FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (tempValue !== value) {
      setValue(tempValue);
      try {
        await onSubmit?.(tempValue);
      } catch (error) {
        console.error('Submit failed:', error);
        setTempValue(value); // rollback
      }
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const startEdit = () => setIsEditing(true);

  return {
    value,
    tempValue,
    isEditing,
    inputRef,
    handleChange,
    handleKeyDown,
    submitEdit,
    cancelEdit,
    startEdit,
  };
};
